import { ChangeEvent, createRef, HTMLAttributes, useEffect, useMemo, useRef, useState } from 'react'
import { useMedia } from 'react-use'
import Flip from 'gsap/dist/Flip'
import { map } from 'lodash'
import Link from 'next/link'
import { useShallow } from 'zustand/shallow'

import Container from '@/components/base/container'
import Grid from '@/components/base/grid'
import HeartIcon from '@/components/base/heart-icon'
import Section from '@/components/base/section'
import Typography from '@/components/base/typography'
import Heading from '@/components/sections/heading'
import Spacer from '@/components/sections/spacer'
import { useAppStore } from '@/contexts/store'
import { useSSRWishlist } from '@/hooks'
import { useLayoutStore } from '@/store/layout'
import { hasFinishing, isInWishlist, useWishlistStore } from '@/store/wishlist'
import { API, MOCK_DATA } from '@/constants'
import { axiosServer as axios } from '@/base/axios'

import { useColorGridMotion } from './motion/color-grid'
import { cn } from '@/lib/utils'
import { Color, ColorSearchResults, WPTaxonomy } from '@/types'

export type ColorsGridProps = HTMLAttributes<HTMLDivElement> &
  Partial<{
    collection: Color[]
    category: WPTaxonomy
  }>

export const useColorsGrid = () => {
  const isSM = useMedia('(max-width: 767px)', true)
  const isMD = useMedia('(min-width: 768px) and (max-width: 1023px)', false)
  const isLG = useMedia('(min-width: 1024px) and (max-width: 1279px)', false)
  return isSM ? 2 : isMD ? 3 : isLG ? 4 : 6
}

const DEBOUNCE_INTERVAL = 500
const MINIMUM_DIGIT = 1

const ColorsGrid = ({ collection: colors, category, ...props }: ColorsGridProps) => {
  const wishlist = useSSRWishlist()
  const [addToWishlist, removeFromWishlist, enableFromWishlist, disableFromWishlist] =
    useWishlistStore(
      useShallow((state) => [
        state.addToWishlist,
        state.removeFromWishlist,
        state.enableFromWishlist,
        state.disableFromWishlist
      ])
    )
  const [searchValue, setSearchValue] = useState('')
  const [searchResults, setSearchResults] = useState<ColorSearchResults | null>(null)
  const [isLoading, setLoading] = useState(false)
  const { strings } = useAppStore()
  const cards = useRef<HTMLDivElement>(null!)
  const typingTimer = useRef<any>(null)

  const collection = useMemo(
    () =>
      map(
        searchResults && searchResults.results?.length ? searchResults.results : colors,
        (color) => ({ ...color, cardRef: createRef<HTMLDivElement>() })
      ),
    [searchResults, colors]
  )

  const { motionRef } = useColorGridMotion(collection)

  const handleChangeInput = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value)
  }

  useEffect(() => {
    if (searchValue.length < MINIMUM_DIGIT) {
      setSearchResults(null)
      return
    }

    clearTimeout(typingTimer.current)
    typingTimer.current = setTimeout(async () => {
      setLoading(true)
      const { data: results } = MOCK_DATA
        ? { data: require('@/data/api/search.json') as ColorSearchResults }
        : await axios.get<ColorSearchResults>(API.COLOR_SEARCH, {
            params: { q: searchValue, cat: category?.term_id, per_page: 20 }
          })
      setSearchResults(results)
      setLoading(false)
    }, DEBOUNCE_INTERVAL)

    return () => {
      clearTimeout(typingTimer.current)
    }
  }, [searchValue, category?.term_id])

  useEffect(() => {
    const unsubscribe = useLayoutStore.subscribe(
      (state) => state.gridLayout,
      (gridLayout) => {
        const className = `grid-layout-${gridLayout}`
        const state = Flip.getState('[data-motion-card]', { simple: true })

        cards.current.classList.remove('grid-layout-0', 'grid-layout-1', 'grid-layout-2')
        cards.current.classList.add(className)

        Flip.from(state, {
          duration: 0,
          stagger: { amount: 0 },
          ease: 'power2.inOut',
          scale: false
        })
      },
      {
        fireImmediately: true
      }
    )

    return () => {
      unsubscribe()
    }
  }, [])

  return (
    <>
      <Spacer variant="2xl" />

      <Heading
        use_search={true}
        search_placeholder={strings?.labels?.search_color}
        onSubmit={(event) => event.preventDefault()}
        onChange={handleChangeInput}
        fetching={isLoading}
      />

      <Spacer variant="sm" />

      <Section ref={motionRef} {...props}>
        <Container className="min-h-[50vh]">
          <Grid ref={cards} className="grid-layout-1 gap-y-gutter">
            {searchResults && searchResults.total === 0 && searchValue.length >= MINIMUM_DIGIT ? (
              <div className="col-span-2 md:col-span-6 lg:col-span-12">
                <Typography variant="history-size-body">{strings?.labels?.no_results}</Typography>
              </div>
            ) : (
              map(collection, ({ cardRef, ...color }) => {
                const { id, title, link, hex_color } = color
                return (
                  <div
                    key={`card-${id}`}
                    className={cn(
                      'group/card relative col-span-1 aspect-[182/220]'
                      /* {
                        'md:col-span-1 lg:col-span-2 xl:col-span-1': gridLayout === 0,
                        'md:col-span-2 lg:col-span-3 xl:col-span-2': gridLayout === 1,
                        'md:col-span-3 lg:col-span-4 xl:col-span-4': gridLayout === 2
                      } */
                    )}
                    data-motion-card
                  >
                    <div ref={cardRef} className="relative size-full">
                      <Link
                        href={link}
                        scroll={false}
                        aria-label={`Apri il colore ${title}`}
                        className={cn(
                          'flex size-full rounded-[0.7rem] border border-[#EBECEA]',
                          'after:absolute after:left-0 after:top-0 after:size-full after:rounded-[0.7rem] after:border-[0rem] after:border-[#EBECEA] after:opacity-40 after:mix-blend-overlay',
                          'after:transition-all after:duration-300 after:ease-out-cubic pointer:group-hover/card:after:border-[1rem]',
                          'focus-ring-dark'
                        )}
                        style={{ backgroundColor: hex_color || '#dddddd' }}
                      />

                      <button
                        className={cn(
                          'absolute bottom-[2rem] z-10 md:bottom-[3rem] lg:bottom-[2rem] xl:bottom-[3rem]',
                          'left-[2rem] right-[2rem] md:left-[3rem] md:right-[3rem] lg:left-[2rem] lg:right-[2rem] xl:left-[3rem] xl:right-[3rem]',
                          'flex items-center justify-between rounded-[.5rem] bg-white/70 p-[1rem]',
                          'transition-colors duration-300 ease-out-cubic',
                          {
                            'bg-dark text-white': isInWishlist(id, wishlist)
                          },
                          'focus-ring-dark'
                        )}
                        onClick={() =>
                          isInWishlist(id, wishlist, true)
                            ? isInWishlist(id, wishlist)
                              ? hasFinishing(id, wishlist)
                                ? disableFromWishlist(id)
                                : removeFromWishlist(id)
                              : enableFromWishlist(id)
                            : addToWishlist({ ...color, finishing: null, enabled: true })
                        }
                      >
                        <Typography variant="upper-small" className="font-bold uppercase">
                          {title.toString()}
                        </Typography>

                        <HeartIcon active={isInWishlist(id, wishlist)} />
                      </button>
                    </div>
                  </div>
                )
              })
            )}
          </Grid>
        </Container>
      </Section>
    </>
  )
}

export default ColorsGrid
